/**
 * THIS COMPONENT IS TO MAKE IMPLEMENTING TABLES MORE UNIFORM
 * IT HAS BECOME QUITE COMPLICATED OVER TIME AND NEEDS TO BE PULLED BACK TO SOMETHING MORE SIMPLE
 *
 * TODO Group multiple inputs into an single object.
 *
 * 2023/08/25
 * Added a closeModal emitter to notify the parent that modal has closed
 *
 * 2023/04/10
 * Actions now has a template input (customActionTemplate) that can be passed from the parent component this makes things easier in terms of permissions and passing data back and forth
 *
 */

// TODO Generate an additional table for nicer printing and exporting that can be used by the excel export and the print function.
// TODO To place the schedule report button where the export buttons are will be nice
/*
The component has been customised to allow for sortable individual columns by adding a sorting value eg:
    {
      field: 'product',
      header: 'Product Name',
      sortable: 'product.name',
      converter: (val: IProduct) => val.name,
    },

  To the parent components column array.

  You can also pass through fixedLayout to get equal sizes on the columns

  Please see the input list below to further understand what is capable
*/

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  CheckboxItem,
  ICustomer,
  IProduct,
  IProfileEntity,
} from '@boma/supplier-portal/shared/entities';
import { HelperFunctionsService } from '@boma/supplier-portal/shared/services';
import autoTable from 'jspdf-autotable';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ExportDocumentService } from '../shared/services/export-document.service';

import { ActivatedRoute } from '@angular/router';
import { getSelectedOrganizationInfo } from '@boma/supplier-portal/shared/state';
import { Store } from '@ngrx/store';

export interface CrudColumn {
  field: string;
  header: string;
  type?: string;
  style?: string;
  sortable?: string;
  tooltip?: string;
  filter?: any;
  converter?: any;
}

@Component({
  selector: 'boma-crud-table',
  templateUrl: './crud-table.component.html',
  styleUrls: ['./crud-table.component.scss'],
})
export class CrudTableComponent implements OnInit, OnChanges {
  @ViewChild('dt') pTableRef: Table;
  @Input() selectedColumns;
  @Input() allowDelete = false;
  @Input() canExport = false; // This was the original idea to be a simple innovative report export enable to export particular sets
  @Input() locationFilter = false;
  @Input() singleLocation = true;
  @Input() productFilter: {
    show: boolean;
    single: boolean;
    initialProducts?: IProduct[];
  } = { show: false, single: true, initialProducts: [] };
  @Input() userFilter = false;
  @Input() customerFilter: {
    show: boolean;
    single: boolean;
    initialCustomers?: ICustomer[];
  } = { show: false, single: true, initialCustomers: [] };
  @Input() createFormRoute;
  @Input() createFormTemplateReference;
  @Input() customActionTemplate;
  @Input() createFormValid = true;
  @Input() customActions;
  @Input() customTableActions;
  @Input() data;
  @Input() displaySearchBar = false;
  @Input() searchBarPlaceHolder = 'Search';
  @Input() fixedLayout = false;
  @Input() headersValue = 'BoMa';
  @Input() singularTerm = '';
  @Input() itemsPerPage = 25;
  @Input() updateFormTemplateReference;
  @Input() viewable = false;
  @Input() nextPage: number = undefined;
  @Input() loaded = null;
  @Input() paginationData = null;
  @Input() queryParam = '';
  @Input() selectableColumns: CrudColumn;
  // Used for identifying confirmation dialog
  @Input() key: string;
  @Input() statusFilter: { options: any[]; label: string } = null;
  @Input() typeFilter: { options: any[]; label: string } = null;
  @Input() filters;
  @Input() filterLabel = 'Filter';
  @Input() viewFormTemplateReference;
  @Input() viewFormTemplateHeading = 'Quick View';
  @Input() dateRequired = false;
  @Input() singleDay = false;
  @Input() selectedDates = {
    fromDate: null,
    toDate: new Date(new Date().setHours(23, 59, 59, 999)).toISOString(),
  };
  @Input() emitDateOnLoad = false;
  @Input() viewPath = null;
  @Input() requiredPermissions: string[] = [];
  @Input() modalSize = "{ width: 'auto' }";
  @Input() createFormTemplateReferenceStyle = "{ width: '450px' }";
  @Input() useSearchButton = false; // user has to click search to update, rather than all updates.
  @Input() containerType = 'box-section';
  @Input() firstIndex = 0;
  @Input() shortcutDate = null;
  @Output() createSubmit = new EventEmitter();
  @Output() updateSubmit = new EventEmitter();
  @Output() updateSelection = new EventEmitter();
  @Output() viewSelection = new EventEmitter();
  @Output() itemDeleted = new EventEmitter();
  @Output() customAction = new EventEmitter();
  @Output() customTableAction = new EventEmitter();
  @Output() queryChanged: EventEmitter<string> = new EventEmitter();
  @Output() nextPageClicked: EventEmitter<boolean> = new EventEmitter();
  @Output() changePageClicked: EventEmitter<boolean> = new EventEmitter();
  @Output() itemsPerPageChanged: EventEmitter<string> = new EventEmitter();
  @Output() locationSelected = new EventEmitter();
  @Output() userSelected = new EventEmitter();
  @Output() customerSelected = new EventEmitter();
  @Output() productSelected = new EventEmitter();
  @Output() datesSelected = new EventEmitter();
  @Output() filterSelected = new EventEmitter();
  @Output() statusSelected = new EventEmitter();
  @Output() typeSelected = new EventEmitter();
  @Output() updateCreateView = new EventEmitter();
  @Output() modalClosed = new EventEmitter();

  private unsubscribe$: Subject<void> = new Subject();

  selectedOrganisation: IProfileEntity;
  searchTermDebouncer: Subject<string> = new Subject<string>();
  displayCreateDialog = false;
  displayUpdateDialog = false;
  displayViewDialog = false;
  displayMapDialog = false;
  selectedPoint: any = null; // potentially just make this be a selected row...
  require: any;
  isFileSaverSupported = false;
  exportColumns: any[];
  selectedRows: any[];
  selectedFilters: any[];
  selectedStatus: any[];
  selectedStatuses: any[];

  @Input() showMultiSelect = false;
  @Input() statusSelector = 'chips'; //
  selectedType: any[];
  perPageOptions = [
    { label: '5', value: '5' },
    { label: '25', value: '25' },
    { label: '50', value: '50' },
    { label: '100', value: '100' },
    { label: '250', value: '250' },
  ];
  statuses: any[];

  // !!TODO NEED TO PUT THESE INTO STATE TO PREVENT CONSTANT
  // Figure out where to put these probably in user state under views and save each views state so that it can be dumped to local storage and reused.
  currentDateEvent = null;
  currentCustomerSelected = null;
  currentProductSelected = null;
  currentStatusSelected = null;
  currentLocationsSelected = null;
  currentUserSelected = null;
  currentFilterSelected = null;
  updatedSearchParams = false;
  enableSearchButton = false;

  constructor(
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private helperService: HelperFunctionsService,
    private exportDocumentService: ExportDocumentService,
    private store: Store,

    public route: ActivatedRoute,
    private helperFunctions: HelperFunctionsService
  ) {
    this.store.select(getSelectedOrganizationInfo).subscribe((x) => {
      this.selectedOrganisation = x;
    });
  }

  ngAfterViewInit = () => {
    const table = this.pTableRef.el.nativeElement.querySelector('table');
    table.setAttribute('id', 'data-table');
  };

  ngOnInit() {
    this.route.queryParams.subscribe((queryParams) => {
      const view = queryParams['view'];

      if (view) this.displayViewDialog = true;
      // Now you can use the stocktake and location values as needed in your component
    });

    if (this.loaded == null) this.loaded = true; // TODO just covers us when loaded is not passed in... to prevent perpetual loader
    this.selectedColumns =
      this.selectedColumns || this.selectableColumns.filter((x) => !x?.hide);

    // FILTERING DATA
    this.statuses = [
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ];

    // END OF FILTERING DATA

    if (this.filters) this.selectedFilters = this.filters[0].value;

    try {
      this.isFileSaverSupported = !!new Blob();
    } catch (e) {}

    if (!this.queryParam) this.queryParam = '';

    this.displayCreateDialog = false;
    this.displayUpdateDialog = false;

    this.searchTermDebouncer
      .pipe(debounceTime(333))
      .subscribe((value) => this.queryChanged.emit(value));

    this.exportColumns = this.selectedColumns.map((col) => ({
      title: col.header,
      dataKey: col.field,
    }));
  }

  searchUpdated(ev = null) {
    this.updatedSearchParams = true;
    if (!this.useSearchButton) this.searchNow();
  }
  searchNow() {
    this.updatedSearchParams = false;
    if (this.limitDateRangeDurationByStatus('Fulfilled') === true) {
      this.messageService.clear();
      this.searchTermDebouncer.next(this.queryParam);
    } else return;
  }

  perPageUpdated(ev) {
    this.itemsPerPageChanged.emit(ev.value);
    this.changePage(1);
  }

  loadNextPage() {
    this.nextPageClicked.emit(true);
  }

  changePage(ev) {
    this.changePageClicked.emit(ev);
  }

  statusFilterHandler(ev) {
    this.statusSelected.emit(ev);
  }

  typeFilterHandler(ev) {
    this.typeSelected.emit(ev);
  }

  handleItemUpdateSelection(data: Object) {
    this.updateSelection.emit(data);
    this.displayUpdateDialog = true;
  }

  handleItemViewSelection(data: Object) {
    this.viewSelection.emit(data);
    this.displayViewDialog = true;
  }

  lazyUpdate(ev) {
    console.log(ev);
    const pageNumber = ev.first === 0 ? 1 : ev.first / ev.rows + 1;

    console.log(pageNumber, this.itemsPerPage);
    this.firstIndex = pageNumber * this.itemsPerPage;

    //check if we are already on the current page;

    if (
      this.paginationData?.nextPage - 1 === pageNumber ||
      this.paginationData?.previousPage + 1 === pageNumber
    )
      return;

    //goto page
    this.changePage(pageNumber);
  }

  handleItemView(data: any, viewable: boolean = false) {
    if (!viewable) return;
    if (this.viewFormTemplateReference) {
      this.displayViewDialog = true;
    } else {
    }
    this.viewSelection.emit(data);
  }

  createItem() {
    this.createSubmit.emit();
    this.displayCreateDialog = false;
  }

  updateItem() {
    this.updateSubmit.emit();
    this.displayUpdateDialog = false;
  }

  deleteItem(data) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected item?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.itemDeleted.emit(data);
      },
      key: this.key,
    });
  }

  exportPdf() {
    // this import feels un needed as its already in the head
    import('jspdf').then((jsPDF) => {
      import('jspdf-autotable').then((x) => {
        const doc = new jsPDF.default('l', 'px'); // landscape pixel

        // @Todo append logo etc report name etc

        doc.setFont('helvetica');
        doc.getStyle('Bold');
        doc.setFontSize(12);

        let y = 10;

        const logo = {
          width: 60,
          height: 20,
          url: 'assets/layout/images/logo-dark.png',
        };
        const margin = {
          default: 20,
          left: 40,
          top: 20,
        };

        const titleText = this.headersValue;
        // POSITIONING LOGO
        const img = new Image();
        // TODO Replace with Supplier Logo
        img.src = logo.url;
        doc.addImage(img, 'png', margin.left, y, logo.width, logo.height);

        doc.text(
          [`${this.headersValue} Report ` + new Date()],
          logo.width + margin.left + margin.default,
          (y = y + logo.height / 2)
        );

        const totalPagesExp = '{total_pages_count_string}';

        autoTable(doc, {
          html: '#data-table',
          startY: y + margin.default,
          theme: 'striped',
          didDrawPage: function (data) {
            // Header for every page
            // Keep! we might need to do this for specific reports.
            // doc.setFontSize(20);
            // doc.setTextColor(40);
            // if (img) {
            //   doc.addImage(
            //     img,
            //     'png',
            //     data.settings.margin.left,
            //     y,
            //     logo.width,
            //     22
            //   );
            // }
            // doc.text(
            //   `${titleText}Report`,
            //   data.settings.margin.left + logo.width + margin.default,
            //   22
            // );

            // Footer
            let str = 'Page ' + doc.internal.pages.length;
            if (typeof doc.putTotalPages === 'function') {
              str = `${str} of ${totalPagesExp}`;
            }
            doc.setFontSize(10);
            const pageSize = doc.internal.pageSize;
            const pageHeight = pageSize.height
              ? pageSize.height
              : pageSize.getHeight();
            doc.text(
              `${str}, ${titleText} Report, ${new Date()}`,
              data.settings.margin.left,
              pageHeight - 10
            );
          },
          margin: { top: 30 },
          // styles: {
          //   minCellHeight: 20,
          // },
        }); // false error on autotable TODO: Investigate later.

        if (typeof doc.putTotalPages === 'function') {
          doc.putTotalPages(totalPagesExp);
        }

        doc.save(`${this.headersValue}_export.pdf`);
      });
    });
  }

  openMap(point) {
    this.selectedPoint = point;
    this.displayMapDialog = true;
  }

  exportExcel() {
    import('xlsx').then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(this.data);

      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = xlsx.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

      this.saveAsExcelFile(excelBuffer, 'excelfilename');
    });
  }

  // If Search parameters have been updated let the filter emit.

  private checkSearchParamsUpdated<T>(
    currentValues: T[],
    newValues: T[],
    emitEvent: EventEmitter<T[]>
  ) {
    console.log(newValues);
    const newUniqueValues =
      this.helperService.removeDuplicatesAndNullsFromArray(newValues);

    if (currentValues !== newUniqueValues) this.updatedSearchParams = true;

    emitEvent.emit(newUniqueValues);
  }

  //TODO IS THIS USED?
  statusHandler(ev) {
    this.checkSearchParamsUpdated(
      this.currentStatusSelected,
      ev,
      this.statusSelected
    );
  }

  handleSelectedSearchParameters(event: CheckboxItem[]) {
    if (this.selectedStatuses == event) return;
    this.updatedSearchParams = true;
    this.selectedStatuses = event;
    this.statusSelected.emit(this.selectedStatuses);
  }

  //Limits the maximum duration you are allowed to search on specified filter
  limitDateRangeDurationByStatus(
    status: string,
    duration = 90,
    dateStart = this.currentDateEvent === null
      ? null
      : this.currentDateEvent[0],
    dateEnd = this.currentDateEvent === null ? null : this.currentDateEvent[1]
  ): boolean {
    var results = true;
    this.selectedStatuses?.some((param) => {
      if (
        (param?.label === status &&
          this.helperFunctions.diffDays(dateStart, dateEnd) > duration) ||
        (param?.label === status && this.currentDateEvent === null)
      ) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Date Range',
          detail: `The maximum date range on ${status} transactions is ${duration} days.`,
          sticky: true,
        });
        results = false;
      }
    });
    return true; // make results again but this is super annoying just optimise the orders
  }

  datesHandler(ev) {
    this.updatedSearchParams = true;
    if (ev[0] === ev[1]) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Dates are identical',
        detail:
          'The dates are exactly the same down to the second, this is a problem.',
      });
    }
    if (this.currentDateEvent === ev) return;
    this.currentDateEvent = ev;
    // we updated the dates so we should clear the results?
    this.datesSelected.emit(ev);
  }

  //write a function that loops through all rows on the table and puts it into a JSON object for export.

  saveAsCSV() {
    let csv = '';
    const csvSeparator = ';';
    const csvHeaders = new Map();

    this.data.forEach((record, i) => {
      for (let i = 0; i < this.selectedColumns.length; i++) {
        const column = this.selectedColumns[i];
        if (column.field) {
          const csvHeader = '"' + column.header + '"';
          if (!csvHeaders.has(column.header)) {
            csvHeaders.set(column.header, csvHeader);
          }

          let cellData;

          if (column.converter) {
            cellData = column.converter(record[column.field]);
          } else {
            cellData = record[column.field];
          }

          if (cellData != null) {
            cellData = String(cellData).replace(/"/g, '""');
          } else {
            cellData = '';
          }
          csv += '"' + cellData + '"';

          if (i < this.selectedColumns.length - 1) {
            csv += csvSeparator;
          }
        }
      }
      csv += '\n';
    });
    csv = Array.from(csvHeaders.values()).join(csvSeparator) + '\n' + csv;
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    });

    if ((window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(
        blob,
        this.headersValue + '_export.csv'
      );
    } else {
      const link = document.createElement('a');
      link.style.display = 'none';
      document.body.appendChild(link);

      if (link.download !== undefined) {
        link.setAttribute('href', URL.createObjectURL(blob));
        link.setAttribute('download', this.headersValue + '_export.csv');
        link.click();
      } else {
        const csvData = 'data:text/csv;charset=utf-8,' + csv;
        const encodedUri = encodeURI(csvData);
        window.open(encodedUri);
      }

      document.body.removeChild(link);
    }
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    import('file-saver').then((FileSaver) => {
      const EXCEL_TYPE =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const EXCEL_EXTENSION = '.xlsx';
      const data: Blob = new Blob([buffer], {
        type: EXCEL_TYPE,
      });
      FileSaver.saveAs(
        data,
        `${this.headersValue}` +
          '_export_' +
          new Date().getTime() +
          EXCEL_EXTENSION
      );
    });
  }

  exportDocument() {
    this.exportDocumentService.exportExcelFromHtml(
      document.getElementById('print-content').innerHTML
    );
  }

  toggleCreateView() {
    this.displayCreateDialog = !this.displayCreateDialog;
    this.updateCreateView.emit(this.displayCreateDialog);
  }

  hideModal(action: string) {
    this.displayCreateDialog = false;
    this.displayUpdateDialog = false;
    this.modalClosed.emit(action);
  }

  ngOnChanges(changes: SimpleChanges): void {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
